import React, { useEffect, useState } from "react";
import { NavLink, useHistory } from "react-router-dom";
// Chakra imports
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  SimpleGrid,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
// Custom components
import DefaultAuth from "layouts/auth/Default";
// Assets
import illustrationGL from "assets/img/auth/auth-gamelight.png";
import { FcGoogle } from "react-icons/fc";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { RiEyeCloseLine } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import {
  SignInAction,
  SignUpAction,
  UpdateSignInRequestState,
} from "../../../listener/signin/action";
import { RootReducer } from "../../../RootReducer";

function SignUp() {
  const dispatch = useDispatch();
  const history = useHistory();

  const requestState = useSelector(
    (state: RootReducer) => state.authentication.signUpRequestState
  );

  // Chakra color mode
  const textColor = useColorModeValue("navy.700", "white");
  const textColorSecondary = "gray.400";
  const textColorDetails = useColorModeValue("navy.700", "secondaryGray.600");
  const textColorBrand = useColorModeValue("brand.500", "white");
  const [show, setShow] = React.useState(false);
  const handleClick = () => setShow(!show);

  const [mail, setMail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [passwordRepeat, setPasswordRepeat] = useState<string>("");
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [companyName, setCompanyName] = useState<string>("");

  const [isCheckboxChecked, setCheckboxChecked] = useState(false);

  const [isCheckboxErr, setIsCheckboxErr] = useState(false);
  const [isPasswordErr, setIsPasswordErr] = useState(false);

  useEffect(() => {
    return () => {
      dispatch(
        UpdateSignInRequestState({
          isFailed: false,
          isLoading: false,
          isSuccessful: false,
        })
      );
    };
  }, [dispatch]);

  useEffect(() => {
    if (localStorage.getItem("jwtToken")) {
      history.push("/ua");
    }
  }, [requestState]);

  useEffect(() => {
    const onEnter = (e: any) => {
      if (e.key === "Enter") {
        if (password !== passwordRepeat) {
          setIsPasswordErr(true);
          return;
        }

        setIsPasswordErr(false);

        dispatch(
          SignUpAction(
            mail,
            password,
            firstName,
            lastName,
            companyName,
            "",
            "",
            "",
            "",
            null
          )
        );
      }
    };

    window.addEventListener("keydown", onEnter);

    return () => {
      window.removeEventListener("keydown", onEnter);
    };
  }, [mail, password, firstName, lastName, companyName]);

  return (
    <DefaultAuth isSignIn={false} illustrationBackground={illustrationGL}>
      <Flex
        //style={{ marginLeft: -50, marginTop: 20}}
        maxW={{ base: "100%", md: "max-content" }}
        w="100%"
        mx={{ base: "auto", lg: "-60px" }}
        me="auto"
        h="100%"
        alignItems="start"
        justifyContent="center"
        mb={{ base: "30px", md: "60px" }}
        px={{ base: "25px", md: "0px" }}
        mt={{ base: "40px", lg: "100px", md: "14vh" }}
        flexDirection="column"
      >
        <Box me="auto">
          <Heading color={textColor} fontSize="36px" mb="10px">
            Register
          </Heading>
          <Text
            mb="36px"
            ms="4px"
            color={textColorSecondary}
            fontWeight="400"
            fontSize="md"
          >
            Start your first Campaign within 3 minutes!
          </Text>
        </Box>
        <Flex
          zIndex="2"
          direction="column"
          w={{ base: "100%", md: "520px" }}
          maxW="100%"
          background="transparent"
          borderRadius="15px"
          mx={{ base: "auto", lg: "unset" }}
          me="auto"
          mb={{ base: "20px", md: "auto" }}
        >
          <FormControl>
            <SimpleGrid columns={2} spacing={3}>
              <div>
                <Input
                  isRequired={true}
                  variant="auth"
                  fontSize="sm"
                  ms={{ base: "0px", md: "0px" }}
                  value={firstName}
                  placeholder={"First Name"}
                  onChange={(c) => setFirstName(c.target.value)}
                  mb="24px"
                  fontWeight="500"
                  size="lg"
                />
              </div>
              <div>
                <Input
                  isRequired={true}
                  variant="auth"
                  fontSize="sm"
                  ms={{ base: "0px", md: "0px" }}
                  placeholder={"Last Name"}
                  value={lastName}
                  onChange={(c) => setLastName(c.target.value)}
                  mb="24px"
                  fontWeight="500"
                  size="lg"
                />
              </div>
            </SimpleGrid>
            <div>
              <Input
                isRequired={true}
                variant="auth"
                fontSize="sm"
                ms={{ base: "0px", md: "0px" }}
                placeholder="Company Name"
                value={companyName}
                onChange={(c) => setCompanyName(c.target.value)}
                mb="24px"
                fontWeight="500"
                size="lg"
              />
            </div>
            <div>
              <Input
                isRequired={true}
                variant="auth"
                fontSize="sm"
                ms={{ base: "0px", md: "0px" }}
                type="email"
                placeholder="Email"
                value={mail}
                onChange={(c) => setMail(c.target.value)}
                mb="24px"
                fontWeight="500"
                size="lg"
              />
            </div>

            <FormControl isInvalid={isPasswordErr || isCheckboxErr}>
              <SimpleGrid columns={2} spacing={3}>
                <div>
                  <InputGroup size="md">
                    <Input
                      isRequired={true}
                      fontSize="sm"
                      placeholder="Password"
                      mb="24px"
                      size="lg"
                      value={password}
                      onChange={(c) => setPassword(c.target.value)}
                      type={show ? "text" : "password"}
                      variant="auth"
                    />
                    <InputRightElement
                      display="flex"
                      alignItems="center"
                      mt="4px"
                    >
                      <Icon
                        color={textColorSecondary}
                        _hover={{ cursor: "pointer" }}
                        as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                        onClick={handleClick}
                      />
                    </InputRightElement>
                  </InputGroup>
                </div>
                <div>
                  <InputGroup size="md">
                    <Input
                      isRequired={true}
                      fontSize="sm"
                      placeholder="Confirm Password"
                      mb="24px"
                      size="lg"
                      value={passwordRepeat}
                      onChange={(c) => setPasswordRepeat(c.target.value)}
                      type={show ? "text" : "password"}
                      variant="auth"
                    />
                    <InputRightElement
                      display="flex"
                      alignItems="center"
                      mt="4px"
                    >
                      <Icon
                        color={textColorSecondary}
                        _hover={{ cursor: "pointer" }}
                        as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                        onClick={handleClick}
                      />
                    </InputRightElement>
                  </InputGroup>
                </div>
                <FormErrorMessage style={{ marginTop: -20, marginBottom: 10 }}>
                  {isPasswordErr
                    ? "Passwords are not matching"
                    : "Terms of Use are not accepted."}
                </FormErrorMessage>
              </SimpleGrid>
            </FormControl>

            <Checkbox
              isChecked={isCheckboxChecked}
              onChange={() => setCheckboxChecked(!isCheckboxChecked)}
              fontSize={10}
              color={"#656565"}
              style={{ marginBottom: 20, fontSize: 12 }}
            >
              <Text style={{ fontSize: 14 }}>
                By signing up you agree to the{" "}
                <a
                  href={"/terms"}
                  target="_blank"
                  style={{ textDecoration: "underline" }} rel="noreferrer"
                >
                  Platform Terms of Use
                </a>
              </Text>
            </Checkbox>

            <Button
              isLoading={requestState.isLoading}
              isDisabled={
                mail === "" ||
                password === "" ||
                companyName === "" ||
                !mail.includes("@")
              }
              onClick={() => {
                if (password !== passwordRepeat) {
                  setIsPasswordErr(true);
                  return;
                } else {
                  setIsPasswordErr(false);
                }
                if (!isCheckboxChecked) {
                  setIsCheckboxErr(true);
                  return;
                } else {
                  setIsCheckboxErr(false);
                }

                dispatch(
                  SignUpAction(
                    mail,
                    password,
                    firstName,
                    lastName,
                    companyName,
                    "",
                    "",
                    "",
                    "",
                    null
                  )
                );
              }}
              fontSize="sm"
              variant="brand"
              fontWeight="500"
              w="100%"
              h="50"
              mb="24px"
            >
              Register
            </Button>
          </FormControl>
          <Flex
            flexDirection="column"
            justifyContent="center"
            alignItems="start"
            maxW="100%"
            mt="0px"
          >
            <Text color={textColorDetails} fontWeight="400" fontSize="14px">
              Already registered?
              <NavLink to="/login">
                <Text
                  color={textColorBrand}
                  as="span"
                  ms="5px"
                  fontWeight="500"
                >
                  Login
                </Text>
              </NavLink>
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </DefaultAuth>
  );
}

export default SignUp;
